import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  FormControl,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';
import { v4 as uuidv4 } from 'uuid';
import { CommonModule, DOCUMENT } from '@angular/common';
import { NgxTippyModule } from 'ngx-tippy-wrapper';
import { IconsComponent } from '../icons/icons.component';
import { ControlEditorDirective } from '../../directives/control-editor.directive';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DOCUMENT_TYPES } from '../../services/document-formatter/document-types';
import { InputNavigationService } from '../../services/InputNavigation/input-navigation.service';
import { Subject, takeUntil } from 'rxjs';
import { DocumentFormatterService } from '../../services/document-formatter/document-formatter.service';

const EMAIL_DOMAIN_SUFFIXES = [
  '@gmail.com',
  '@outlook.com',
  '@yahoo.com',
  '@icloud.com',
  '@hotmail.com',
];

@Component({
  selector: 'mg-input',
  standalone: true,
  imports: [
    CommonModule,
    NgxTippyModule,
    ReactiveFormsModule,
    IconsComponent,
    ControlEditorDirective,
    MatTooltipModule,
  ],
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class InputComponent implements OnInit, OnDestroy {
  @ViewChild('inputElement') inputElement!: ElementRef;
  private _documentType: typeof DOCUMENT_TYPES[0] | null = null;

  get documentType(): (typeof DOCUMENT_TYPES)[0] | null {
    return this._documentType;
  }

  @Input()
  set documentType(value: (typeof DOCUMENT_TYPES)[0] | null) {
    this._documentType = value;
    if (value) {
      let initialValue = '';
      switch (value.id) {
        case 'dni':
          initialValue = '0';
          break;
        case 'cpj':
          initialValue = '3';
          break;
        case 'enr':
          initialValue = '9';
          break;
        case 'dimex':
          initialValue = '1';
          break;
        case 'didi':
          initialValue = '5';
          break;
        default:
          initialValue = '';
      }
      const formattedValue = this.documentFormatter.applyMask(
        initialValue,
        value.mask,
        value.id
      );
      this.control.setValue(formattedValue);
    } else {
      this.control.setValue('');
    }
  }

  @ViewChild('tippyInstance') tippyInstance!: ElementRef<HTMLDivElement>;
  @ViewChild('input') inputRef!: ElementRef;

  @Input() id = '';
  @Input() label = '';
  @Input() disabled = false;
  @Input() placeholder = '';
  @Input() required = false;
  @Input() type:
    | 'text'
    | 'password'
    | 'color'
    | 'email'
    | 'number'
    | 'image'
    | 'month'
    | 'tel'
    | 'time'
    | 'week' = 'text';
  @Input() autocompleteType:
    | 'email'
    | 'cc-csc'
    | 'cc-number'
    | 'cc-exp'
    | 'cc-exp-year'
    | 'cc-exp-month'
    | 'postal-code'
    | 'off'
    | 'street-address'
    | 'country-name'
    | 'address-line1'
    | 'name'
    | 'username'
    | 'address-level2'
    | 'given-name'
    | 'family-name'
    | 'one-time-code' = 'off';
  @Input() control: FormControl = new FormControl({
    value: '',
    disabled: this.disabled,
  });
  @Input() value = '';
  @Input() maxLength = '';
  @Input() tooltip = '';
  @Input() mgIcon = '';
  @Input() showValidator = false;
  @Input() mgTitle: string | undefined;
  @Input() mgTitlev2: string | null = null;
  @Input() errors: { [key: string]: string } = {};
  @Input() showOptions = false;
  @Input() options: any[] | undefined;
  @Input() isLoadingOptions = false;
  @Input() currency?: string;
  @Input() suggestEmailDomains = false;
  @Input() clearable = false;

  @Output() blurred = new EventEmitter<void>();
  @Output() focusEmitter = new EventEmitter<FocusEvent>();
  @Output() iconClick = new EventEmitter<void>();
  @Output() selectedOptionEmitter = new EventEmitter<any>();
  @Output() suggestionsVisibilityChange = new EventEmitter<boolean>();
  @Input() nextInputId?: string;
  @Input() prevInputId?: string;
  @Input() enableSmartNavigation = false;
  @Input() inputGroupName?: string;
  @Input() autofocus = false;
  private readonly destroy$ = new Subject<void>();
  @Output() focused = new EventEmitter<void>();

  active = false;
  isFocused = false;
  originalType = '';

  lastInputLength = 0;

  constructor(
    private readonly _elementRef: ElementRef,
    private readonly documentFormatter: DocumentFormatterService,
    private readonly navigationService: InputNavigationService,
    @Inject(DOCUMENT) private readonly _document: Document
  ) {}

  ngOnInit(): void {
    if (!this.id.length) {
      this.id = `input-${uuidv4()}`;
    }

    if (this.type === 'password') {
      this.originalType = 'password';
    }

    if (this.enableSmartNavigation) {
      this.navigationService.registerInput(this.id, {
        nextInputId: this.nextInputId,
        prevInputId: this.prevInputId,
        groupName: this.inputGroupName,
      });

      this.control.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => {
          if (this.enableSmartNavigation && this.maxLength && !this.disabled) {
            const maxLen = parseInt(this.maxLength);
            if (value && typeof value === 'string' && value.length >= maxLen) {
              if (this.nextInputId) {
                this.navigationService.navigateToInput(
                  this.nextInputId,
                  this.id
                );
              } else if (this.inputGroupName) {
                this.navigationService.navigateToNextInGroup(
                  this.inputGroupName,
                  this.id
                );
              }
            }
          }
        });
    }

    this.control.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this._document.activeElement === this.inputRef?.nativeElement) {
        this.isFocused = true;
      }
    });

    if (this.suggestEmailDomains) {
      this.control.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => this.updateEmailSuggestions(value));
    }
  }

  private updateEmailSuggestions(value: unknown): void {
    const text = typeof value === 'string' ? value : '';
    const shouldShow = !!text && !text.includes('@');

    this.options = shouldShow
      ? EMAIL_DOMAIN_SUFFIXES.map((suffix) => ({ name: text + suffix }))
      : [];

    const visible = this.options.length > 0;
    if (visible !== this.showOptions) {
      this.showOptions = visible;
      this.suggestionsVisibilityChange.emit(visible);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onInputClick() {
    this.isFocused = true;
  }

  onFocus() {
    this.isFocused = true;
    this.focused.emit();
    if (this.control.value && typeof this.control.value === 'string') {
      this.lastInputLength = this.control.value.length;
    }
  }

  @HostListener('paste')
  onPaste(): void {
    setTimeout(() => {
      this.checkNavigationAfterValueChange();
    }, 50);
  }

  private checkNavigationAfterValueChange(): void {
    if (!this.enableSmartNavigation || this.disabled) return;

    const currentValue = this.control.value;
    if (currentValue && typeof currentValue === 'string' && this.maxLength) {
      const maxLen = parseInt(this.maxLength);

      // Si el valor ha alcanzado o superado la longitud máxima, navegamos al siguiente campo
      if (currentValue.length >= maxLen) {
        if (this.nextInputId) {
          this.navigationService.navigateToInput(this.nextInputId, this.id);
        } else if (this.inputGroupName) {
          this.navigationService.navigateToNextInGroup(
            this.inputGroupName,
            this.id
          );
        }
      }
    }
  }

  @HostListener('document:click', ['$event'])
  onClick(event: Event) {
    if (!this._elementRef.nativeElement.contains(event.target)) {
      setTimeout(() => {
        this.isFocused = false;
      }, 150);
    }
  }

  @HostListener('input', ['$event'])
  onInputEvent(event: Event): void {
    this.isFocused = true;
    if (!this.documentType) return;

    const inputEvent = event as InputEvent;
    const input = event.target as HTMLInputElement;
    const cursorPosition = input.selectionStart || 0;

    const value = input.value.replace(/[^a-zA-Z0-9]/g, '');
    const prevValue = this.control.value;
    const formattedValue = this.documentFormatter.applyMask(
      value,
      this.documentType.mask,
      this.documentType.id
    );

    this.control.setValue(formattedValue, { emitEvent: false });

    let newCursorPosition = cursorPosition;
    const specialCharsBefore = (
      formattedValue.substring(0, newCursorPosition).match(/[^a-zA-Z0-9]/g) ||
      []
    ).length;
    const originalSpecialCharsBefore = (
      prevValue.substring(0, newCursorPosition).match(/[^a-zA-Z0-9]/g) || []
    ).length;

    newCursorPosition += specialCharsBefore - originalSpecialCharsBefore;

    if (
      inputEvent.inputType === 'insertText' &&
      newCursorPosition < formattedValue.length
    ) {
      if (/[^a-zA-Z0-9]/.test(formattedValue.charAt(newCursorPosition - 1))) {
        newCursorPosition++;
      }
    }

    newCursorPosition = Math.min(
      formattedValue.length,
      Math.max(0, newCursorPosition)
    );

    requestAnimationFrame(() => {
      input.setSelectionRange(newCursorPosition, newCursorPosition);
    });
    // Validación
    const isValid = this.documentFormatter.validatePattern(
      formattedValue,
      this.documentType.pattern
    );
    if (formattedValue && !isValid) {
      this.control.setErrors({ pattern: true });
    } else {
      this.control.setErrors(null);
    }

    if (
      inputEvent.inputType === 'insertFromPaste' ||
      formattedValue.length - this.lastInputLength > 1
    ) {
      this.checkNavigationAfterValueChange();
    }
    this.lastInputLength = formattedValue.length;
  }

  @HostListener('keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent): void {
    this.isFocused = true;

    if (!this.enableSmartNavigation || this.disabled) return;

    const inputElement = event.target as HTMLInputElement;

    if (this.id === 'cardNumber' && event.key === 'Backspace') {
      // Permitir el borrado normal sin navegación automática
      return;
    }

    if (event.key === 'Backspace' && inputElement.value === '') {
      if (this.prevInputId) {
        event.preventDefault();
        this.navigationService.navigateToInputEnd(this.prevInputId, this.id);
      } else if (this.inputGroupName) {
        event.preventDefault();
        this.navigationService.navigateToPrevInGroup(
          this.inputGroupName,
          this.id
        );
      }
    }

    // Flecha derecha en última posición -> siguiente input
    if (
      event.key === 'ArrowRight' &&
      inputElement.selectionStart === inputElement.value.length &&
      inputElement.selectionEnd === inputElement.value.length
    ) {
      if (this.nextInputId) {
        event.preventDefault();
        this.navigationService.navigateToInput(this.nextInputId);
      } else if (this.inputGroupName) {
        event.preventDefault();
        this.navigationService.navigateToNextInGroup(
          this.inputGroupName,
          this.id
        );
      }
    }

    // Flecha izquierda en primera posición -> input anterior
    if (
      event.key === 'ArrowLeft' &&
      inputElement.selectionStart === 0 &&
      inputElement.selectionEnd === 0
    ) {
      if (this.prevInputId) {
        event.preventDefault();
        this.navigationService.navigateToInputEnd(this.prevInputId);
      } else if (this.inputGroupName) {
        event.preventDefault();
        this.navigationService.navigateToPrevInGroup(
          this.inputGroupName,
          this.id
        );
      }
    }
  }

  getErrors(controlErrors: ValidationErrors | null): string {
    if (!controlErrors) return '';

    if (controlErrors['serverError']) {
      return controlErrors['serverError'];
    }

    const firstError = Object.keys(controlErrors)[0];

    return this.errors[firstError];
  }

  get error() {
    return this.control.invalid && this.control.touched;
  }

  onBlur(): void {
    setTimeout(() => {
      if (
        !this._elementRef.nativeElement.contains(this._document.activeElement)
      ) {
        this.isFocused = false;
      }
    }, 50);
    this.blurred.emit();
  }

  onActive() {
    this.active = true;
  }

  onInactive() {
    this.active = false;
  }

  handleSelect(option: any) {
    if (this.suggestEmailDomains) {
      this.control.setValue(option.name);
      this.showOptions = false;
    }
    this.selectedOptionEmitter.emit(option);
    this.isFocused = false;
  }

  focus(event: FocusEvent): void {
    //this.inputElement.nativeElement.focus();
    this.focusEmitter.emit(event);
  }

  togglePasswordVisibility() {
    this.type = this.type === 'password' ? 'text' : 'password';
  }

  get iconName() {
    return this.type === 'password' ? 'eye' : 'eye-off';
  }

  get setIconValidIndicator() {
    return !this.error ? 'invalid' : 'valid';
  }

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.value = input.value;
  }

  get nativeElement() {
    return this.inputRef.nativeElement;
  }

  onIconClick(): void {
    this.iconClick.emit();
  }

  setFocus(): void {
    this.inputRef.nativeElement.focus();
  }

  clearInput(): void {
    if (this.disabled) return;
    this.control.setValue('');
    this.value = '';
    this.setFocus();
  }
}
